import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Lock, Mail, Loader2, ArrowLeft } from "lucide-react";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user && user !== false) navigate("/admin/dashboard", { replace: true });
  }, [user, navigate]);

  const formatDetail = (d) => {
    if (!d) return "Login failed.";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((e) => e.msg || JSON.stringify(e)).join(" ");
    return JSON.stringify(d);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Email and password are required.");
      return;
    }
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back.");
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-white/[0.04] border border-white/10 rounded-xl pl-11 pr-4 py-3.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#007AFF] focus:ring-1 focus:ring-[#007AFF] transition-colors";

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-[#050505] text-white grid place-items-center px-6 relative overflow-hidden"
    >
      <div className="absolute -top-40 -left-40 h-[420px] w-[420px] rounded-full bg-[#007AFF]/20 blur-[140px]" />
      <div className="absolute -bottom-40 -right-40 h-[420px] w-[420px] rounded-full bg-[#00F0FF]/10 blur-[140px]" />
      <div className="absolute inset-0 ring-grid opacity-30" />

      <a
        href="/"
        data-testid="admin-login-back"
        className="absolute top-6 left-6 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Back to site
      </a>

      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="relative w-full max-w-md glass-strong rounded-3xl p-8 md:p-10"
      >
        <div className="flex items-center gap-2 mb-8">
          <span className="relative h-8 w-8 grid place-items-center rounded-full bg-[#007AFF]/20 border border-[#007AFF]/40">
            <span className="h-2 w-2 rounded-full bg-[#00F0FF] animate-pulse-glow" />
          </span>
          <span className="font-display text-lg font-extrabold tracking-tight">
            Brand<span className="text-[#00F0FF]">With</span>Abdo
          </span>
        </div>

        <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#00F0FF]">
          / Admin
        </div>
        <h1 className="mt-3 font-display text-3xl md:text-4xl font-extrabold tracking-[-0.02em]">
          Sign in to view leads.
        </h1>
        <p className="mt-3 text-slate-400 text-sm">
          Restricted area. Authorised access only.
        </p>

        <div className="mt-8 space-y-5">
          <div className="relative">
            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input
              data-testid="admin-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              autoComplete="username"
              placeholder="Admin email"
              className={inputCls}
              required
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type="password"
              autoComplete="current-password"
              placeholder="Password"
              className={inputCls}
              required
            />
          </div>
        </div>

        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={submitting}
          className="mt-8 w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-[#007AFF] text-white font-semibold hover:bg-[#00F0FF] hover:text-black disabled:opacity-60 disabled:cursor-not-allowed transition-all"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Signing in...
            </>
          ) : (
            "Sign in"
          )}
        </button>
      </form>
    </div>
  );
}
